// =============================================================================
// src/features/crisis/crisis-evo-sampler.ts
// Amostragem/interpolacao pura do grafico de evolucao (sem DOM).
//
// Proveniencia: porte FIEL de assets/crisis.js 112-132 (lerp/fmt/timeLabel/
//   sample). Separado do controller de hover para manter funcoes 4-20 linhas e
//   permitir teste headless da matematica (nenhuma chamada de DOM aqui).
//
// SRP: so calculo numerico. O controller (crisis-evo-slider.ts) injeta a
//   geometria e a serie; este modulo nao toca o DOM nem o estado de UI.
// =============================================================================

import type { EvoAreaGeometry } from './crisis-chart-geometry';

/** Resultado de uma amostra ao longo da curva de evolucao. */
export interface EvoSample {
  /** Posicao X em unidades do viewBox. */
  readonly vbX: number;
  /** Valor de mencoes interpolado entre pontos vizinhos. */
  readonly value: number;
  /** Posicao Y em unidades do viewBox (segue a linha). */
  readonly vbY: number;
  /** Posicao fracionaria no eixo (i + t) para o rotulo de tempo. */
  readonly pos: number;
}

/** Interpola linearmente `a`->`b` em `t` (assets/crisis.js 112). */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Arredonda e formata em pt-BR (assets/crisis.js 113). */
export function formatMentions(value: number): string {
  return Math.round(value).toLocaleString('pt-BR');
}

/** Rotulo de tempo relativo a partir da posicao fracionaria (assets/crisis.js 114-117). */
export function timeLabel(pos: number): string {
  const ago = 23 - pos;
  return ago < 0.5 ? 'agora' : `há ${Math.round(ago)}h`;
}

/**
 * Amostra a curva numa posicao X em pixels relativa ao plot. Faz clamp no
 * intervalo do viewBox, escolhe o segmento [i, i+1] e interpola valor e Y
 * (assets/crisis.js 119-132).
 */
export function sampleEvoAt(
  relX: number,
  plotWidth: number,
  geometry: EvoAreaGeometry,
  data: readonly number[],
): EvoSample {
  const n = data.length;
  const xs = geometry.coords.map((c) => c[0]);
  const vbX = clampViewBoxX((relX / plotWidth) * geometry.w, xs, n);
  const i = clampSegmentIndex(Math.floor((vbX - geometry.padX) / geometry.step), n);
  const t = clampUnit(segmentProgress(vbX, xs, i));
  return buildSample(vbX, t, i, geometry, data);
}

/** Clamp de vbX entre o primeiro e o ultimo X (assets/crisis.js 121). */
function clampViewBoxX(vbX: number, xs: readonly number[], n: number): number {
  const minX = xs[0] ?? 0;
  const maxX = xs[n - 1] ?? 0;
  if (vbX < minX) return minX;
  if (vbX > maxX) return maxX;
  return vbX;
}

/** Mantem o indice de segmento em [0, n-2] (assets/crisis.js 123). */
function clampSegmentIndex(i: number, n: number): number {
  if (i < 0) return 0;
  if (i > n - 2) return n - 2;
  return i;
}

/** Progresso 0..1 dentro do segmento i (assets/crisis.js 124). */
function segmentProgress(vbX: number, xs: readonly number[], i: number): number {
  const x0 = xs[i] ?? 0;
  const x1 = xs[i + 1] ?? x0;
  const span = x1 - x0 || 1;
  return (vbX - x0) / span;
}

/** Clamp em [0, 1] (assets/crisis.js 125). */
function clampUnit(t: number): number {
  if (t < 0) return 0;
  if (t > 1) return 1;
  return t;
}

/** Monta a amostra interpolando valor e Y entre os pontos i e i+1 (assets/crisis.js 126-131). */
function buildSample(
  vbX: number,
  t: number,
  i: number,
  geometry: EvoAreaGeometry,
  data: readonly number[],
): EvoSample {
  const v0 = data[i] ?? 0;
  const v1 = data[i + 1] ?? v0;
  const y0 = geometry.coords[i]?.[1] ?? 0;
  const y1 = geometry.coords[i + 1]?.[1] ?? y0;
  return { vbX, value: lerp(v0, v1, t), vbY: lerp(y0, y1, t), pos: i + t };
}
